import './app.scss';
import domready from 'domready';
import 'lazysizes';
import {loadLazyImages} from './utilities/js/lazy-image-loader';
import {EventScroller} from './utilities/js/event-scroller';
import {ResizeHandler} from './utilities/js/resize-handler';
import {MediaMatchHandler} from './utilities/js/media-match-handler';
import Siteheader from './layout/siteheader/siteheader';
import {CookieConsent} from "./components/cookie-consent/cookie-consent";
import {checkAndLoadFiles} from './utilities/js/helper';

const OPTIONS = {
    breakpoints: {
        s: 320,
        m: 768,
        l: 1024,
        xl: 1280,
        xxl: 1440,
        xxxl: 1920
    }
};

window.eventScroller = new EventScroller();
window.resizeHandler = new ResizeHandler();
window.mediaMatchHandler = new MediaMatchHandler();

window.youtubeApi = false;
window.youtubeApiLoading = false;
window.krpanoApi = false;
window.siteheaderAPI = null;

const browserCheck = () => {
    window.browser = {
        csshover: !window.matchMedia('(pointer:coarse)').matches,
        ios: (/iPhone|iPad|iPod/i).test(navigator.userAgent),
        iphone: (/iPhone|iPod/i).test(navigator.userAgent),
        android: (/(android)/i).test(navigator.userAgent),
        firefox: (/firefox/i).test(navigator.userAgent),
        safari: (/Version\/[\d.]+.*Safari/).test(navigator.userAgent)
    };

    Object.entries(window.browser).forEach(([key, value]) => {
        document.documentElement.classList.add((!value) ? `no-${key}` : `${key}`);
    });
};

const initContent = ($content) => {
    checkAndLoadFiles($content).finally(() => {
        loadLazyImages();

        window.checkInView = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (!entry.target.classList.contains('inview')) {
                        entry.target.classList.add('inview');

                        if (typeof entry.target.dataset.footer === 'undefined') {
                            window.inViewObserver.unobserve(entry.target);
                        }
                    }

                    if (typeof entry.target.dataset.footer !== 'undefined') {
                        quickAccessSwitch('off');
                    }
                } else {
                    if (typeof entry.target.dataset.footer !== 'undefined') {
                        quickAccessSwitch('on');
                    }
                }
            });
        };

        window.inViewObserver = new IntersectionObserver(window.checkInView, {
            threshold: 0.1
        });

        const $quickAccess = document.querySelector('[data-quick-access="root"]');
        const quickAccessSwitch = (state) => {
            if (!$quickAccess) {
                return;
            }

            if (state === 'off') {
                $quickAccess.classList.add('is--hidden');
            }
            if (state === 'on') {
                $quickAccess.classList.remove('is--hidden');
            }
        };

        const $$forms = $content.querySelectorAll('[data-form="root"]');
        if ($$forms.length > 0) {
            import('./components/form/form-handler').then(({FormHandler}) => {
                for (let i = 0; i < $$forms.length; i++) {
                    const $form = $$forms[i];
                    $form.API = new FormHandler($form);
                }
            });
        }

        const $$cartAdd = $content.querySelectorAll('[data-cart-add="root"]');
        if ($$cartAdd.length > 0) {
            import('./components/cart/cart-add').then(({CartAdd}) => {
                for (let i = 0; i < $$cartAdd.length; i++) {
                    const $cartAdd = $$cartAdd[i];
                    $cartAdd.API = new CartAdd($cartAdd, {
                        onSubmit: ($content) => {
                            initContent($content);
                        }
                    });
                }
            });
        }

        const $$cartUpdate = $content.querySelectorAll('[data-cart-update="root"]');
        if ($$cartUpdate.length > 0) {
            import('./components/cart/cart-update').then(({CartUpdate}) => {
                for (let i = 0; i < $$cartUpdate.length; i++) {
                    const $cartUpdate = $$cartUpdate[i];
                    $cartUpdate.API = new CartUpdate($cartUpdate, {
                        loader: 'cart-item__loader',
                        onSubmit: ($content) => {
                            initContent($content);
                        }
                    });
                }
            });
        }

        const $$cartRemove = $content.querySelectorAll('[data-cart-remove="root"]');
        if ($$cartRemove.length > 0) {
            import('./components/cart/cart-remove').then(({CartRemove}) => {
                for (let i = 0; i < $$cartRemove.length; i++) {
                    const $cartRemove = $$cartRemove[i];
                    $cartRemove.API = new CartRemove($cartRemove, {
                        loader: 'cart-item__loader',
                        onSubmit: ($content) => {
                            initContent($content);
                        }
                    });
                }
            });
        }

        const $$minicart = $content.querySelectorAll('[data-minicart="root"]');
        if ($$minicart.length > 0) {
            import('./layout/minicart/minicart').then(({Minicart}) => {
                for (let i = 0; i < $$minicart.length; i++) {
                    const $minicart = $$minicart[i];
                    $minicart.API = new Minicart($minicart, {
                        onSubmit: ($content) => {
                            initContent($content);
                        }
                    });
                }
            });
        }

        const $$productOverview = $content.querySelectorAll('[data-product-overview="root"]');
        if ($$productOverview.length > 0) {
            import('./pages/product/product-overview/product-overview').then(({ProductOverview}) => {
                for (let i = 0; i < $$productOverview.length; i++) {
                    const $productOverview = $$productOverview[i];
                    const productOverviewAPI = new ProductOverview($productOverview, {
                        onSubmit: ($content) => {
                            initContent($content);
                        }
                    });

                    $productOverview.API = productOverviewAPI;
                }
            });
        }

        const $$cookieConsent = $content.querySelectorAll('[data-cookie-consent="root"]');
        if ($$cookieConsent.length > 0) {
            import('./components/cookie-consent/cookie-consent').then(({CookieConsent}) => {
                for (let i = 0; i < $$cookieConsent.length; i++) {
                    const $cookieConsent = $$cookieConsent[i];
                    $cookieConsent.API = new CookieConsent($cookieConsent);
                }
            });
        }

        const $$modal = $content.querySelectorAll('[data-modal]');
        if ($$modal.length > 0) {
            import('./components/modal/modal').then(({Modal}) => {
                for (let i = 0; i < $$modal.length; i++) {
                    const $modal = new Modal($$modal[i], {
                        onAjaxLoaded: ($content) => {
                            initContent($content);
                        }
                    });

                    $modal.initialize();
                }
            });
        }

        const $$tracking = $content.querySelectorAll('[data-tracking="root"]');
        if ($$tracking.length > 0) {
            import('./components/tracking/tracking').then(({Tracking}) => {
                for (let i = 0; i < $$tracking.length; i++) {
                    const $tracking = new Tracking($$tracking[i], {
                        onAjaxLoaded: ($content) => {
                            initContent($content);
                        }
                    });

                    $tracking.initialize();
                }
            });
        }
    });
};

domready(function () {
    const initApplication = () => {
        browserCheck();

        initContent(document);

        window.eventScroller.init();
        window.resizeHandler.init();

        const $siteheader = document.querySelector('[data-siteheader="root"]');
        if ($siteheader) {
            const siteheaderAPI = new Siteheader($siteheader);
            siteheaderAPI.initialize();
            window.siteheaderAPI = siteheaderAPI;
        }

        const $tooltip = document.querySelector('[data-tooltip="root"]');
        if ($tooltip) {
            import('./components/tooltip/tooltip').then(({Tooltip}) => {
                const tooltipAPI = new Tooltip($tooltip);
                tooltipAPI.initialize();
                window.siteheaderAPI = tooltipAPI;
            });
        }

        const contentUpdateEvent = new CustomEvent('content.loaded', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: document
            }
        });

        document.body.classList.add('page--loaded');

        window.dispatchEvent(contentUpdateEvent);

        window.dataInview = document.querySelectorAll('[data-inview]:not(.inview)');

        setTimeout(() => {
            window.dataInview.forEach((dataInView) => {
                window.inViewObserver.observe(dataInView);
            });
        }, 500);
    };

    initApplication();
});
